import { Heading } from '@radix-ui/themes';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { FormSelect } from 'components/form/select';
import { FormTextField } from 'components/form/textfield';
import { ActionSelect } from 'components/layout/action-select';
import { EB } from 'components/layout/error-boundary';
import { RevenueStructureForm } from 'components/main/routes/revenue-structures/form';
import { CommonTable } from 'components/table';
import { AuthContext } from 'contexts/auth';
import { DialogType } from 'enums/dialog';
import { DialogState } from 'interfaces/dialog/state';
import { OptionDef } from 'interfaces/options';
import { BaseDoc } from 'lib/interfaces/database/_base';
import { RevenueStructureWhole } from 'lib/interfaces/database/revenue-structure';
import { DateUtil } from 'lib/utilities/date';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useMemo, useState } from 'react';
import useRevStructStore from 'stores/revenue-structures';
import useSecuritiesStore from 'stores/securities';

export const RevenueStructures = () => {
  const { data: securities, load: loadSecurities } = useSecuritiesStore();
  const {
    create,
    data: revStructs,
    filters,
    load: loadRevStructs,
    setFilters,
    update,
  } = useRevStructStore();

  const { perms } = useContext(AuthContext);
  const { securities: crudSecurities, revenueStructures: crudRevStructs } =
    perms;

  useEffect(() => {
    if (crudSecurities?.read) {
      loadSecurities();
    }
  }, [crudSecurities?.read, loadSecurities]);

  useEffect(() => {
    if (crudRevStructs?.read) {
      loadRevStructs();
    }
  }, [crudRevStructs?.read, loadRevStructs]);

  const filteredData = useMemo(() => {
    return revStructs
      .filter(
        (m) =>
          !filters.effective ||
          DateUtil.isSameDay(filters.effective, m.effective)
      )
      .filter(
        (m) => !filters.securityID || filters.securityID === m.securityID
      );
  }, [revStructs, filters]);

  const [dialog, setDialog] = useState<DialogState>();

  const securityOptions = useMemo<OptionDef[]>(() => {
    return securities.map((o) => ({
      label: o.name,
      value: o.id,
      group: o._issuer?.name,
    }));
  }, [securities]);

  return (
    <EB>
      <Heading>Revenue Structures</Heading>
      <CommonTable
        defaultSort={{
          iCol: 2,
          asc: false,
        }}
        toolbarControls={[
          <FormTextField
            label="Effective Date"
            defaultValue={StringUtil.printDate(filters.effective)}
            onOptionalDateChange={(_, vd) =>
              setFilters({
                effective: vd,
              })
            }
            className="width-250px"
            hideLabel
          />,
          <FormSelect
            label="Security"
            defaultValue={filters.securityID}
            options={securityOptions}
            placeholder="Select a Security"
            onOptionalChange={(v) => setFilters({ securityID: v })}
            className="width-250px"
            hideLabel
          />,
        ]}
        toolbarActions={[
          {
            hidden: !crudRevStructs?.create,
            label: 'Create',
            onClick: () => {
              setDialog({
                key: Date.now(),
                type: DialogType.Create,
                data: {},
              });
            },
          },
        ]}
        columns={[
          {
            header: 'Actions',
            rowFn: (item: RevenueStructureWhole) => (
              <ActionSelect
                options={[
                  {
                    hidden: !crudRevStructs?.update,
                    label: 'Update',
                    value: DialogType.Update,
                  },
                ]}
                onValueChange={(v) => {
                  // new value never gets set but it does trigger the effect below
                  switch (v) {
                    case DialogType.Update: {
                      setDialog({
                        key: Date.now(),
                        type: v,
                        data: item,
                      });
                      break;
                    }

                    default: {
                      break;
                    }
                  }
                }}
              />
            ),
          },
          {
            header: 'Created',
            rowFn: (item: RevenueStructureWhole) =>
              StringUtil.printDateTime(item.createdAt),
            sortFn: (a: RevenueStructureWhole, b: RevenueStructureWhole) =>
              SortUtil.dates(a.createdAt, b.createdAt),
          },
          {
            header: 'Effective Date',
            rowFn: (item: RevenueStructureWhole) =>
              StringUtil.printDate(item.effective),
            sortFn: (a: RevenueStructureWhole, b: RevenueStructureWhole) =>
              SortUtil.dates(a.effective, b.effective),
          },
          {
            header: 'Security',
            rowFn: (item: RevenueStructureWhole) => item._security?.name,
            sortFn: (a: RevenueStructureWhole, b: RevenueStructureWhole) =>
              SortUtil.dates(a._security?.name, b._security?.name),
          },
          {
            header: 'Rate',
            rowFn: (item: RevenueStructureWhole) =>
              StringUtil.printPercent(item.rate),
            sortFn: (a: RevenueStructureWhole, b: RevenueStructureWhole) =>
              SortUtil.numbers(a.rate, b.rate),
          },
          {
            header: 'Recognized',
            rowFn: (item: RevenueStructureWhole) => item.recognized,
            sortFn: (a: RevenueStructureWhole, b: RevenueStructureWhole) =>
              SortUtil.strings(a.recognized, b.recognized),
          },
          {
            header: 'Period',
            rowFn: (item: RevenueStructureWhole) => item.period,
            sortFn: (a: RevenueStructureWhole, b: RevenueStructureWhole) =>
              SortUtil.strings(a.period, b.period),
          },
        ]}
        data={filteredData}
      />

      {dialog?.type === DialogType.Create && (
        <CommonFormDialog
          key={dialog.key}
          title="Create"
          children={<RevenueStructureForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => create(item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Update && (
        <CommonFormDialog
          key={dialog.key}
          title="Update"
          children={<RevenueStructureForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: async (item) => {
              return await update((dialog.data as BaseDoc).id, item);
            },
          }}
          onClose={() => setDialog(undefined)}
        />
      )}
    </EB>
  );
};
